import React, {
  useCallback,
} from 'react';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  getEntityType,
  capitalizeFirstLetter,
} from 'web-utils';

import {
  Video,
  Dropdown,
  MarkdownRenderer,
} from 'web-components';

import {
  Flex,
  H200,
} from 'web-styled';

import * as S from './styled';

type Props = {
  readOnly: boolean,
  parentId: number,
  workoutId: number,
  parentType: string,
  resourceType: string,
}

const WorkoutHeader = ({
  readOnly,
  parentId,
  workoutId,
  parentType,
  resourceType,
}: Props) => {
  const mapState = useCallback(state => ({
    workout: coreSelectors.getResourceById(
      resourceType,
      workoutId,
    )(state),
    folderRoots: coreSelectors.getFolderRoots(state),
  }), [workoutId]);

  const { workout, folderRoots } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();

  const type = getEntityType(resourceType);
  const cpType = capitalizeFirstLetter(type);

  const handleRequestUpdate = (payload) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id: workoutId,
        payload,
        requestType: parentType,
        resourceType,
      }),
    );
  };

  const handleSave = () => {
    let folderId = workout.folder_id;
    if (workout.is_warmup) {
      folderId = folderRoots.warmupId;
    } else if (workout.is_cooldown) {
      folderId = folderRoots.cooldownId;
    }
    dispatch(
      coreActions.requestDuplicateEntity({
        ids: [workoutId],
        folderIds: [folderId],
        resourceType,
      }),
    );
  };

  const handleRemove = () => {
    const payload = type === 'warmup'
      ? { warmup_id: null }
      : { cooldown_id: null };
    dispatch(
      coreActions.requestUpdateEntity({
        id: parentId,
        payload,
        resourceType: parentType,
      }),
    );
  };

  const actions = [
    {
      name: `Save ${cpType} To Library`,
      onClick: handleSave,
    },
    {
      name: `Remove ${cpType} From Workout`,
      onClick: handleRemove,
    },
  ];

  return (
    <Flex width="100%">
      <S.Video>
        <Video
          id={workout.video_id}
          src={workout.video_url}
          title={workout.name}
          entityId={workoutId}
          readOnly={readOnly}
          isRounded
          requestType={parentType}
          resourceType={resourceType}
          height="250px"
        />
      </S.Video>
      <Flex
        width="100%"
        column
        padding="15px 15px 15px 5px"
        spacing={10}
      >
        <Flex width="100%" justify="space-between">
          {readOnly ? (
            <H200 color="primary">{workout.name}</H200>
          ) : (
            <S.TitleInput
              defaultValue={workout.name}
              onSave={(name) => {
                handleRequestUpdate({ name });
              }}
              onSaveEnter={(name) => {
                handleRequestUpdate({ name });
              }}
            />
          )}
          {!readOnly && (
            <Dropdown
              trigger={(
                <S.Actions>
                  <S.ContextMenu />
                </S.Actions>
              )}
              items={actions}
            />
          )}
        </Flex>
        {readOnly ? (
          <MarkdownRenderer
            source={workout?.description}
          />
        ) : (
          <S.Description
            id={workout?.id}
            placeholder="Start typing a description here..."
            source={workout?.description}
            onChange={(description) => {
              handleRequestUpdate({ description });
            }}
          />
        )}
      </Flex>
    </Flex>
  );
};

export default WorkoutHeader;
